import { Navigate, Route, BrowserRouter, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/layout/AppShell';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { AuthProvider, useAuth } from '@/features/auth/useAuth';
import { LoginPage } from '@/features/auth/LoginPage';
import { SuspendedScreen } from '@/features/auth/SuspendedScreen';
import { DeviceListPage } from '@/features/devices/DeviceListPage';
import { FinancePage } from '@/features/finance/FinancePage';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { BoxBalanceGate } from '@/features/cash-drawer/BoxBalanceGate';
import { CashDrawerPage } from '@/features/cash-drawer/CashDrawerPage';
import { CafeRoutes } from '@/features/cafe/CafeRoutes';
import { BillsPage } from '@/features/bills/BillsPage';
import { OutstandingPage } from '@/features/outstanding/OutstandingPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { ReportViewPage } from '@/features/reports/ReportViewPage';
import { OverallReportPage } from '@/features/reports/OverallReportPage';
import { PaymentsByEmployeePage } from '@/features/reports/PaymentsByEmployeePage';
import { BoxResetArchivePage } from '@/features/reports/BoxResetArchivePage';
import { VenueTheme } from '@/features/venue/VenueTheme';
import { PortalContentRoutes } from '@/features/portal-content/PortalContentRoutes';
import { SettingsRoutes } from '@/features/settings/SettingsRoutes';
import { BookingsPage } from '@/features/bookings/BookingsPage';
import { CardsPage } from '@/features/cards/CardsPage';
import { DayPassesPage } from '@/features/dayPasses/DayPassesPage';
import { StorePage } from '@/features/store/StorePage';
import { ScreensPage } from '@/features/screens/ScreensPage';
import { SafePage } from '@/features/safe/SafePage';
import { BondsPage } from '@/features/safe/BondsPage';
import { ChangePasswordPage } from '@/features/account/ChangePasswordPage';
import { ForcedPasswordChangeRoutes } from '@/features/account/ForcedPasswordChangePage';
import { AuditLogPage } from '@/features/audit/AuditLogPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();

  if (status === 'loading') {
    return <div className="grid h-full place-items-center text-content-muted">…</div>;
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Exported for the venue-service gates' behaviour test, which renders it under a MemoryRouter. */
export function AppRoutes() {
  const { status, mustChangePassword } = useAuth();

  // The venue's EclipseGG service is suspended (EclipseGG platform,
  // 2026-10-19): one screen on every path, in place of pages whose every
  // request the server now refuses. Ahead of the routes, so not even the
  // sign-in page's redirect leads past it.
  if (status === 'suspended') return <SuspendedScreen />;

  // A password the seller set (or reset) must be replaced first: the server
  // refuses every other staff route until it is, so the app's frame is not
  // rendered at all — every path leads to the change form.
  if (status === 'authenticated' && mustChangePassword) return <ForcedPasswordChangeRoutes />;

  return (
    <Routes>
      <Route
        path="/login"
        element={status === 'authenticated' ? <Navigate to="/devices" replace /> : <LoginPage />}
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Device List is the landing screen: it is what employees look at all
            shift, and it is the only primary tab with no permission gate. */}
        <Route index element={<Navigate to="/devices" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/devices" element={<DeviceListPage />} />
        <Route path="/cafe/*" element={<CafeRoutes />} />
        <Route path="/customers" element={<CustomersPage />} />
        {/* The venue's one cash drawer (2026-10-12). No route gate: the page
            shows each control only to the people the API lets use it. */}
        <Route path="/cash-drawer" element={<CashDrawerPage />} />
        {/* The box screen until the owner starts the drawer, a redirect to it
            from then on — and Shifts is the drawer now. Kept as routes so a
            bookmark or a habit lands somewhere honest. */}
        <Route path="/box-balance" element={<BoxBalanceGate />} />
        <Route path="/shifts" element={<Navigate to="/cash-drawer" replace />} />
        <Route path="/cards" element={<CardsPage />} />
        {/* SPEC §6.1 — Boot Camp day passes. */}
        <Route path="/day-passes" element={<DayPassesPage />} />
        {/* Both screens gate themselves on bills.view / reports.view, and the
            API refuses the same keys — the route is the convenience, not the
            control (SPEC §3). */}
        <Route path="/bills" element={<BillsPage />} />
        <Route path="/outstanding" element={<OutstandingPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/overall" element={<OverallReportPage />} />
        {/* Payments taken by employee (owner, 2026-10-12): its own page, not a
            catalogue id — it reads payments by when they were taken. Static,
            so it outranks /reports/:reportId the way /overall does. */}
        <Route path="/reports/payments-by-employee" element={<PaymentsByEmployeePage />} />
        {/* The box balance's reset history, read-only since the cash drawer
            replaced it. Static, so it outranks /reports/archived/:legacyId. */}
        <Route path="/reports/archived/box-resets" element={<BoxResetArchivePage />} />
        {/* One report on its own page, back arrow only (owner request).
            Static /overall outranks the param route by v6 ranking. */}
        <Route path="/reports/archived/:legacyId" element={<ReportViewPage />} />
        <Route path="/reports/:reportId" element={<ReportViewPage />} />
        <Route path="/expenses" element={<FinancePage />} />
        {/* /sales removed: the Dashboard owns those figures (owner-approved). */}
        <Route path="/store" element={<StorePage />} />
        <Route path="/portal-content/*" element={<PortalContentRoutes />} />
        <Route path="/screens" element={<ScreensPage />} />
        <Route path="/bookings" element={<BookingsPage />} />
        {/* Wave 3 §12.1 — the safe and the unified Bonds ledger. */}
        <Route path="/safe" element={<SafePage />} />
        <Route path="/bonds" element={<BondsPage />} />
        {/* SPEC §3 — read-only; the page and GET /audit-log both ask for audit.view. */}
        <Route path="/audit-log" element={<AuditLogPage />} />
        {/* Muscle-memory redirect; the model is named Booking (WAVE2-DESIGN §1). */}
        <Route path="/reservations" element={<Navigate to="/bookings" replace />} />
        {/* Printers are managed in Settings; the tab is a pointer, not a copy.
            It followed §6.1 to its new page when that page was split out of
            General Settings — a redirect that still lands on the venue's logo
            is a broken tab, not a working one. */}
        <Route path="/printers" element={<Navigate to="/settings/printing" replace />} />
        <Route path="/settings/*" element={<SettingsRoutes />} />
        {/* Every employee's own account — no permission gate: changing your
            own password needs only the password (owner, 2026-09-23). */}
        <Route path="/account" element={<Navigate to="/account/password" replace />} />
        <Route path="/account/password" element={<ChangePasswordPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/devices" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VenueTheme />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
